/* eslint-disable test-selectors/textfield */
import React, { useEffect, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import Typography from 'housecall-ui/dist/DesignSystem/DataDisplay/Typography/Typography';
import Alert from 'housecall-ui/dist/DesignSystem/Feedback/Alert/Alert';
import CircularProgress from 'housecall-ui/dist/DesignSystem/Feedback/Progress/CircularProgress';
import Button from 'housecall-ui/dist/DesignSystem/Inputs/Buttons/Button';
import TextField from 'housecall-ui/dist/DesignSystem/Inputs/TextFields/TextField';
import Box from 'housecall-ui/dist/DesignSystem/Layout/Box/Box';
import Grid from 'housecall-ui/dist/DesignSystem/Layout/Grid/Grid';
import MenuItem from 'housecall-ui/dist/DesignSystem/Navigation/Menus/MenuItem';
import Collapse from 'housecall-ui/dist/DesignSystem/Transitions/Transition/Collapse';

import HCGoogleMapsAutoComplete from 'components/HCGoogleMapsAutoComplete/HCGoogleMapsAutoComplete';
import {
  AddressFields,
  fetchUpdatePlanPreview,
  organizationAddressHash,
  updateOrganizationAddress,
} from 'components/PayWall/Redux/Actions';
import { PayWallFullState, PayWallOrganizationAddress } from 'components/PayWall/Redux/Reducer.type';
import { getOrganizationAddress } from 'components/PayWall/Redux/Selectors';
import { useTrackEventsService } from 'components/Services/Analytics/useTrackEventsService';
import DataEnrichmentIcon from 'components/Setup/DataEnrichmentIcon';

import OrganizationApi from '../../Resources/Organization/OrganizationApi';

import { CIRCULAR_PROGRESS_BUTTON_SIZE, PAY_WALL_VERSION, useButtonProgressStyles } from './PayWallDialog';

export const countryOptions = [
  { label: 'United States', value: 'US' },
  { label: 'Canada', value: 'CA' },
];

const availableCountries = countryOptions.map((country) => country.value);

export const states = [
  { label: 'Alabama', value: 'AL' },
  { label: 'Alaska', value: 'AK' },
  { label: 'Arizona', value: 'AZ' },
  { label: 'Arkansas', value: 'AR' },
  { label: 'California', value: 'CA' },
  { label: 'Colorado', value: 'CO' },
  { label: 'Connecticut', value: 'CT' },
  { label: 'Delaware', value: 'DE' },
  { label: 'District of Columbia', value: 'DC' },
  { label: 'Florida', value: 'FL' },
  { label: 'Georgia', value: 'GA' },
  { label: 'Hawaii', value: 'HI' },
  { label: 'Idaho', value: 'ID' },
  { label: 'Illinois', value: 'IL' },
  { label: 'Indiana', value: 'IN' },
  { label: 'Iowa', value: 'IA' },
  { label: 'Kansas', value: 'KS' },
  { label: 'Kentucky', value: 'KY' },
  { label: 'Louisiana', value: 'LA' },
  { label: 'Maine', value: 'ME' },
  { label: 'Maryland', value: 'MD' },
  { label: 'Massachusetts', value: 'MA' },
  { label: 'Michigan', value: 'MI' },
  { label: 'Minnesota', value: 'MN' },
  { label: 'Mississippi', value: 'MS' },
  { label: 'Missouri', value: 'MO' },
  { label: 'Montana', value: 'MT' },
  { label: 'Nebraska', value: 'NE' },
  { label: 'Nevada', value: 'NV' },
  { label: 'New Hampshire', value: 'NH' },
  { label: 'New Jersey', value: 'NJ' },
  { label: 'New Mexico', value: 'NM' },
  { label: 'New York', value: 'NY' },
  { label: 'North Carolina', value: 'NC' },
  { label: 'North Dakota', value: 'ND' },
  { label: 'Ohio', value: 'OH' },
  { label: 'Oklahoma', value: 'OK' },
  { label: 'Oregon', value: 'OR' },
  { label: 'Pennsylvania', value: 'PA' },
  { label: 'Rhode Island', value: 'RI' },
  { label: 'South Carolina', value: 'SC' },
  { label: 'South Dakota', value: 'SD' },
  { label: 'Tennessee', value: 'TN' },
  { label: 'Texas', value: 'TX' },
  { label: 'Utah', value: 'UT' },
  { label: 'Vermont', value: 'VT' },
  { label: 'Virginia', value: 'VA' },
  { label: 'Washington', value: 'WA' },
  { label: 'West Virginia', value: 'WV' },
  { label: 'Wisconsin', value: 'WI' },
  { label: 'Wyoming', value: 'WY' },
];

export const canadaStates = [
  { label: 'Alberta', value: 'AB' },
  { label: 'British Columbia', value: 'BC' },
  { label: 'Manitoba', value: 'MB' },
  { label: 'New Brunswick', value: 'NB' },
  { label: 'Newfoundland and Labrador', value: 'NL' },
  { label: 'Nova Scotia', value: 'NS' },
  { label: 'Ontario', value: 'ON' },
  { label: 'Prince Edward Island', value: 'PE' },
  { label: 'Quebec', value: 'QC' },
  { label: 'Saskatchewan', value: 'SK' },
  { label: 'Northwest Territories', value: 'NT' },
  { label: 'Yukon', value: 'YT' },
  { label: 'Nunavut', value: 'NU' },
];

type OrgAddressCountry = 'US' | 'CA';

type OrgAddressFormValues = {
  street?: string;
  unitNumber?: string;
  zipCode?: string;
  city?: string;
  state?: string;
  country?: OrgAddressCountry;
  latitude?: number;
  longitude?: number;
};

type OrgAddressErrors = Partial<Record<'street' | 'unitNumber' | 'zipCode' | 'city' | 'state' | 'country', string>>;

export const isCanada = (country?: OrgAddressCountry) => country === 'CA';
export const usZipCodeRegExp = /(^\d{5}$)|(^\d{5}-\d{4}$)/;
export const canadianZipCodeRegExp = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;

export const validateAddressForm = (values: OrgAddressFormValues) => {
  const errors: OrgAddressErrors = {};

  if (values.country && !availableCountries.includes(values.country)) {
    errors.country = 'Only US and Canadian addresses are currently supported.';
  }

  if (!values.country) {
    errors.country = 'Country is required';
  }

  if (!values.street) {
    errors.street = 'Street address is required';
  }

  if (!values.city) {
    if (isCanada(values.country)) {
      errors.city = 'Municipality is required';
    } else {
      errors.city = 'City is required';
    }
  }

  if (!values.state) {
    if (isCanada(values.country)) {
      errors.state = 'Province is required';
    } else {
      errors.state = 'State is required';
    }
  }

  if (values.state) {
    if (isCanada(values.country)) {
      if (!canadaStates.some((state) => state.value === values.state)) {
        errors.state = 'Province is required';
      }
    }

    if (!isCanada(values.country)) {
      if (!states.some((state) => state.value === values.state)) {
        errors.state = 'State is required';
      }
    }
  }

  if (!values.zipCode) {
    if (isCanada(values.country)) {
      errors.zipCode = 'Postal code is required';
    } else {
      errors.zipCode = 'Zip code is required';
    }
  }

  if (values.zipCode) {
    if (isCanada(values.country)) {
      if (!canadianZipCodeRegExp.test(values.zipCode)) {
        errors.zipCode = 'Invalid postal code';
      }
    }

    if (!isCanada(values.country)) {
      if (!usZipCodeRegExp.test(values.zipCode)) {
        errors.zipCode = 'Invalid zip';
      }
    }
  }

  return errors;
};

const getFormValuesFromOrgAddress = (orgAddress: PayWallOrganizationAddress): OrgAddressFormValues => ({
  street: orgAddress.street,
  country: orgAddress.country as OrgAddressCountry,
  city: orgAddress.city,
  state: orgAddress.state,
  zipCode: orgAddress.postal_code,
  longitude: orgAddress.longitude as number,
  latitude: orgAddress.latitude as number,
  unitNumber: orgAddress.street_line_2 ?? '',
});

export const useOrgAddressFormValues = () => {
  const address = useSelector(getOrganizationAddress);
  return getFormValuesFromOrgAddress(address);
};

export const useOrgAddressFormInvalid = () => {
  const values = useOrgAddressFormValues();
  const errors = validateAddressForm(values);

  return Object.keys(errors).length > 0;
};

export const useOrgDisplayableAddress = () => {
  const address = useSelector(getOrganizationAddress);

  return (
    [
      [address.street, address.street_line_2].filter(Boolean).join(' '),
      address.city,
      [address.state, address.postal_code].filter(Boolean).join(' '),
    ]
      .filter(Boolean)
      .join(', ') || '(empty)'
  );
};

export const useIsAddressTaxJarVerified = () => {
  const taxCalculated = useSelector((state: PayWallFullState) => state.payWall.planSummaryPreview.tax_calculated);
  const validTaxAddress = useSelector((state: PayWallFullState) => state.payWall.planSummaryPreview.valid_tax_address);

  return taxCalculated && validTaxAddress;
};

const PayWallEditableAddress = (): JSX.Element => {
  const [edit, setIsEdit] = useState(false);

  const initialValues = useOrgAddressFormValues();
  const displayableAddress = useOrgDisplayableAddress();
  const isAddressInvalid = useOrgAddressFormInvalid();

  const { trackEvent, trackButtonClicked } = useTrackEventsService();

  useEffect(() => {
    if (isAddressInvalid) {
      setIsEdit(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dispatch = useDispatch();
  const classes = useButtonProgressStyles({ circularProgressSize: CIRCULAR_PROGRESS_BUTTON_SIZE });

  const handleFormSubmit = async (values: OrgAddressFormValues) => {
    try {
      const response = await OrganizationApi.updateOrganization(
        organizationAddressHash(
          {
            street: values.street,
            city: values.city,
            state: values.state,
            postal_code: values.zipCode,
            longitude: values.longitude,
            latitude: values.latitude,
            country: values.country,
          },
          values.unitNumber
        )
      );

      trackEvent('PayWall company address updated', {
        ...values,
        payWallVersion: PAY_WALL_VERSION,
      });

      updateOrganizationAddress(response.data, response.data.street_line_2, dispatch);
      setIsEdit(false);
      dispatch(fetchUpdatePlanPreview());
    } catch (error) {
      // eslint-disable-next-line no-alert
      alert('There was an error saving this address. Please make sure the address form is correct.');
      trackEvent('PayWall company address error', {
        payWallVersion: PAY_WALL_VERSION,
      });
    }
  };

  return (
    <Form onSubmit={handleFormSubmit} validate={validateAddressForm} initialValues={initialValues}>
      {({ handleSubmit, submitting, form, hasValidationErrors, invalid, values, dirty }) => (
        <form onSubmit={handleSubmit} id="paywall-edit-org-address-form" style={{ width: '100%' }}>
          <Grid container>
            <Box
              mt={3}
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
              data-testid={edit ? 'paywall-edit-org-address-form__edit' : 'paywall-edit-org-address-form__display'}
            >
              <Typography variant="h3">Company address</Typography>
              <Box mt={2}>
                <Collapse in={!edit}>
                  <Typography>{displayableAddress}</Typography>
                  <Box mt={2}>
                    <Button
                      color="primary"
                      variant="outlined"
                      onClick={() => {
                        setIsEdit(true);
                        trackButtonClicked({ buttonName: 'Change address PayWall', payWallVersion: PAY_WALL_VERSION });
                      }}
                    >
                      Change address
                    </Button>
                  </Box>
                </Collapse>
                <Collapse in={edit}>
                  <Box width={1}>
                    <Collapse in={hasValidationErrors && dirty}>
                      <Box
                        mb={1.5}
                        data-testid="paywall-org-address-error-alert"
                        aria-hidden={!hasValidationErrors || !dirty}
                      >
                        <Alert severity="error">Please provide your company address</Alert>
                      </Box>
                    </Collapse>
                  </Box>
                  <Grid container spacing={16}>
                    <Grid item xs={8}>
                      <Field name="street">
                        {({ input, meta }) => (
                          <HCGoogleMapsAutoComplete
                            // eslint-disable-next-line react/jsx-props-no-spreading
                            {...input}
                            helperText={meta.error}
                            error={Boolean(meta.error)}
                            id="addressAutocomplete"
                            floatingLabelText="Street"
                            useLatestMaterialUI
                            data-testid="paywall-org-address-street-autocomplete-input"
                            onAutoComplete={(data: AddressFields) => {
                              form.batch(() => {
                                form.change('city', data.city);
                                form.change('street', data.street);
                                form.change('state', data.state);
                                form.change('country', (data.country as OrgAddressCountry) ?? 'US');
                                form.change('zipCode', data.postal_code || data.zip);
                                form.change('latitude', data.latitude);
                                form.change('longitude', data.longitude);
                              });

                              trackEvent('PayWall company address autocomplete triggered', {
                                ...values,
                                payWallVersion: PAY_WALL_VERSION,
                              });
                            }}
                            onTextFieldChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              form.change('street', e.target.value);
                            }}
                            value={input.value}
                            endAdornment={<DataEnrichmentIcon show="googleIcon" />}
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid item xs={4}>
                      <Field name="unitNumber">
                        {({ input, meta }) => (
                          <TextField
                            // eslint-disable-next-line react/jsx-props-no-spreading
                            {...input}
                            helperText={meta.error}
                            error={Boolean(meta.error)}
                            label="Unit #"
                            value={input.value}
                            fullWidth
                            data-testid="paywall-org-address-unit-number-input"
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid item xs={8}>
                      <Field name="city">
                        {({ input, meta }) => (
                          <TextField
                            // eslint-disable-next-line react/jsx-props-no-spreading
                            {...input}
                            helperText={meta.error}
                            error={Boolean(meta.error)}
                            label={isCanada(values.country) ? 'Municipality' : 'City'}
                            value={input.value}
                            fullWidth
                            data-testid="paywall-org-address-city-input"
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid item xs={4}>
                      <Field name="state">
                        {({ input, meta }) => (
                          <TextField
                            // eslint-disable-next-line react/jsx-props-no-spreading
                            {...input}
                            select
                            helperText={meta.error}
                            error={Boolean(meta.error)}
                            label={isCanada(values.country) ? 'Province' : 'State'}
                            value={input.value}
                            fullWidth
                            data-testid="paywall-org-address-state-input"
                          >
                            {(isCanada(values.country) ? canadaStates : states).map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                      </Field>
                    </Grid>
                    <Grid item xs={4}>
                      <Field name="zipCode">
                        {({ input, meta }) => (
                          <TextField
                            // eslint-disable-next-line react/jsx-props-no-spreading
                            {...input}
                            helperText={meta.error}
                            error={Boolean(meta.error)}
                            label={isCanada(values.country) ? 'Postal code' : 'Zip'}
                            value={input.value}
                            fullWidth
                            data-testid="paywall-org-address-zipcode-input"
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid item xs={4}>
                      <Field name="country">
                        {({ input, meta }) => (
                          <TextField
                            // eslint-disable-next-line react/jsx-props-no-spreading
                            {...input}
                            select
                            helperText={meta.error}
                            error={Boolean(meta.error)}
                            label="Country"
                            value={input.value}
                            fullWidth
                            data-testid="paywall-org-address-country-input"
                          >
                            {countryOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                      </Field>
                    </Grid>
                    <Grid item container xs={12}>
                      <Box display="flex" justifyContent="flex-end" width={1}>
                        <Button
                          disabled={submitting}
                          onClick={() => {
                            trackEvent('PayWall company address form dismissed', {
                              ...values,
                              payWallVersion: PAY_WALL_VERSION,
                            });

                            setIsEdit(false);
                            form.restart();
                          }}
                        >
                          Cancel
                        </Button>
                        <Box ml={1} className={classes.wrapper}>
                          <Button disabled={submitting || invalid} type="submit" color="primary" variant="outlined">
                            Confirm
                          </Button>
                          {submitting && (
                            <CircularProgress className={classes.buttonProgress} size={CIRCULAR_PROGRESS_BUTTON_SIZE} />
                          )}
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Collapse>
              </Box>
            </Box>
          </Grid>
        </form>
      )}
    </Form>
  );
};

export default PayWallEditableAddress;
